<script setup>
import { ref, computed, onMounted, onBeforeUnmount, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useAppToast } from '@/shared/lib/useAppToast'

import Button from 'primevue/button'
import ProductImage from '@/shared/ui/ProductImage.vue'

import { getCatalogProductFamily } from '@/modules/catalog/api/catalog.service'
import { useProductVariantConfigurator } from '@/modules/catalog/composables/useProductVariantConfigurator'
import { getEffectiveCatalogImages } from '@/modules/catalog/model/catalogImages'
import { useCartStore } from '@/modules/cart/store/cart.store'
import { useAuthStore } from '@/modules/auth/store/auth.store'
import AddToCartModal from '@/modules/cart/components/AddToCartModal.vue'

const route = useRoute()
const router = useRouter()

const cartStore = useCartStore()
const authStore = useAuthStore()
const toast = useAppToast()

const family = ref(null)
const legacyProduct = ref(null)
const configurator = useProductVariantConfigurator()
const { selectedValues, variantAttributes, matchingProductsCount, loading: variantLoading, error: variantError } = configurator
const product = computed(() => hasVariantAttributes.value ? configurator.selectedProduct.value : legacyProduct.value)

const loading = ref(false)
const cartLoading = ref(false)
const addToCartLoading = ref(false)

const selectedImage = ref(null)
const selectedImageIndex = ref(0)
const showImageModal = ref(false)
const showAddToCartModal = ref(false)

let loadRequestId = 0
let internalNavigationVersion = 0
const internalNavigations = new Map()

function normalizeId(value) {
  const id = Number(value)
  return Number.isInteger(id) && id > 0 ? id : null
}

function getProductId(item) {
  return normalizeId(item?.id ?? item?.Id)
}

function getVariantValues(item) {
  const values = item?.variantValues ?? item?.VariantValues
  return Array.isArray(values) ? values : []
}

function getValueAttributeId(value) {
  return normalizeId(
    value?.attributeId ??
    value?.AttributeId ??
    value?.productAttributeId ??
    value?.ProductAttributeId
  )
}

function getValueId(value) {
  return normalizeId(value?.valueId ?? value?.ValueId)
}

function getValueText(value) {
  return value?.value ?? value?.Value ?? value?.selectedValue ?? '—'
}

function getValueAttributeName(value) {
  return value?.attributeName ?? value?.AttributeName ?? value?.name ?? '-'
}

const familyProducts = computed(() => {
  const products = family.value?.products ?? family.value?.Products
  return Array.isArray(products) ? products : []
})

const commonAttributes = computed(() => {
  const attributes = family.value?.commonAttributes ?? family.value?.CommonAttributes
  return Array.isArray(attributes) ? attributes : []
})

const hasVariantAttributes = computed(() => {
  const attributes = family.value?.variantAttributes ?? family.value?.VariantAttributes ?? []
  return attributes.length > 0
})

const needsExplicitProductSelection = computed(() => {
  return !hasVariantAttributes.value && familyProducts.value.length > 1
})

const explicitProductId = computed({
  get() {
    return product.value?.id ?? product.value?.Id ?? null
  },
  set(value) {
    const nextProduct = familyProducts.value.find(
      item => getProductId(item) === normalizeId(value)
    )

    if (nextProduct) {
      selectProduct(nextProduct)
    }
  }
})

const displayedArticul = computed(() => {
  return product.value?.articul ?? product.value?.Articul ?? '-'
})

const displayedPrice = computed(() => {
  if (!product.value) return '—'

  const value =
    product.value.effectivePriceKzt ??
    product.value.EffectivePriceKzt

  return formatPrice(value)
})

const displayedOldPrice = computed(() => {
  if (!product.value) return null

  const saleIsActive =
    product.value.saleIsActive ??
    product.value.SaleIsActive ??
    false

  const salePrice =
    product.value.salePrice ??
    product.value.SalePrice ??
    null

  const salePriceKzt =
    product.value.salePriceKzt ??
    product.value.SalePriceKzt ??
    null

  if (!saleIsActive || salePrice == null || salePriceKzt == null) {
    return null
  }

  const basePrice =
    product.value.basePriceKzt ??
    product.value.BasePriceKzt

  return formatPrice(basePrice)
})

const modalProduct = computed(() => {
  if (!product.value) return null

  const productType =
    family.value?.productType ??
    family.value?.ProductType ??
    {}

  return {
    ...product.value,

    id: getProductId(product.value),

    name:
      productType.name ??
      productType.Name ??
      'Без названия',

    brandName:
      productType.brandName ??
      productType.BrandName ??
      '',

    categoryName:
      productType.categoryName ??
      productType.CategoryName ??
      '',

    defaultImageUrl:
      productImages.value[0]?.url ?? null,

    effectivePriceKzt:
      product.value.effectivePriceKzt ??
      product.value.EffectivePriceKzt ??
      null,

    priceKzt:
      product.value.effectivePriceKzt ??
      product.value.EffectivePriceKzt ??
      null,

    basePriceKzt:
      product.value.basePriceKzt ??
      product.value.BasePriceKzt ??
      null,

    salePrice:
      product.value.salePrice ??
      product.value.SalePrice ??
      null,

    salePriceKzt:
      product.value.salePriceKzt ??
      product.value.SalePriceKzt ??
      null,

    saleIsActive:
      product.value.saleIsActive ??
      product.value.SaleIsActive ??
      false
  }
})

const productImages = computed(() => {
  if (!product.value) return []
  return getEffectiveCatalogImages(product.value)
})

const displayedAttributes = computed(() => {
  const result = []

  for (const attr of commonAttributes.value) {
    result.push({
      id:
        attr?.attributeId ??
        attr?.AttributeId ??
        attr?.id ??
        attr?.Id,
      name:
        attr?.attributeName ??
        attr?.AttributeName ??
        attr?.name ??
        attr?.Name ??
        '-',
      selectedValue:
        attr?.value ??
        attr?.Value ??
        attr?.selectedValue ??
        attr?.SelectedValue ??
        '—'
    })
  }

  for (const value of getVariantValues(product.value)) {
    result.push({
      id:
        getValueId(value) ??
        `${getValueAttributeId(value)}-${getValueText(value)}`,
      name: getValueAttributeName(value),
      selectedValue: getValueText(value)
    })
  }

  return result
})

const canAddToCart = computed(() => {
  if (!product.value) return false

  if (hasVariantAttributes.value && (variantLoading.value || variantError.value)) return false

  return true
})

function getAttributeId(attribute) {
  return normalizeId(
    attribute?.attributeId ??
    attribute?.AttributeId ??
    attribute?.id ??
    attribute?.Id
  )
}

function getAttributeName(attribute) {
  return (
    attribute?.attributeName ??
    attribute?.AttributeName ??
    attribute?.name ??
    attribute?.Name ??
    '-'
  )
}

function getAttributeValues(attribute) {
  const values =
    attribute?.values ??
    attribute?.Values ??
    attribute?.availableValues ??
    attribute?.AvailableValues

  return Array.isArray(values) ? values : []
}

function getAttributeValueGroups(attribute) {
  const groups = new Map()
  for (const value of getAttributeValues(attribute)) {
    const metadata = value.group ?? value.Group
    const id = metadata?.valueId ?? metadata?.ValueId ?? null
    const name = metadata?.name ?? metadata?.Name ?? null
    const identity = id ?? metadata?.key ?? metadata?.Key ?? null
    const key = identity != null && name ? identity : null
    if (!groups.has(key)) groups.set(key, { id: key, name: key == null ? null : name, values: [] })
    groups.get(key).values.push(value)
  }
  return [...groups.values()]
}

function isValueAvailable(attribute, value) {
  return (value?.available ?? value?.Available) === true
}

function resetGallery() {
  selectedImageIndex.value = 0
  selectedImage.value = productImages.value[0]?.url ?? null
}

function updateProductRoute(nextProduct) {
  const nextId = getProductId(nextProduct)
  if (nextId != null && nextId !== normalizeId(route.params.id)) {
    const navigationId = ++internalNavigationVersion
    internalNavigations.set(navigationId, nextId)
    const finish = () => internalNavigations.delete(navigationId)
    router.replace({ name: route.name, params: { ...route.params, id: nextId }, query: route.query, hash: route.hash })
      .then(finish, finish)
  }
}

function selectProduct(nextProduct, updateRoute = true) {
  legacyProduct.value = nextProduct
  if (updateRoute) updateProductRoute(nextProduct)
}

function selectAttributeValue(attribute, value) {
  return configurator.selectValue(getAttributeId(attribute), getValueId(value))
}

watch(product, nextProduct => {
  resetGallery()
  showImageModal.value = false
  showAddToCartModal.value = false
  if (hasVariantAttributes.value && nextProduct) updateProductRoute(nextProduct)
}, { flush: 'sync' })

function isAttributeValueSelected(attribute, value) {
  const attributeId = getAttributeId(attribute)
  const valueId = getValueId(value)

  return (
    attributeId != null &&
    valueId != null &&
    normalizeId(selectedValues.value[attributeId]) === valueId
  )
}

async function loadFamily(productId) {
  const requestId = ++loadRequestId
  configurator.reset()
  legacyProduct.value = null
  family.value = null
  const normalizedProductId = normalizeId(productId)
  if (!normalizedProductId) {
    loading.value = false
    return
  }
  try {
    loading.value = true
    const loadedFamily = await getCatalogProductFamily(normalizedProductId)
    if (requestId !== loadRequestId) return
    family.value = loadedFamily
    const products = loadedFamily?.products ?? loadedFamily?.Products ?? []
    const requestedId = normalizeId(loadedFamily?.requestedProductId ?? loadedFamily?.RequestedProductId ?? normalizedProductId)
    const defaultId = normalizeId(loadedFamily?.defaultProductId ?? loadedFamily?.DefaultProductId)
    // Identity lookup only: availability and combination resolution always belong to #43.
    const requestedProduct = products.find(item => getProductId(item) === requestedId)
    if (hasVariantAttributes.value) {
      loading.value = false
      const type = loadedFamily.productType ?? loadedFamily.ProductType
      await configurator.initialize(type.id ?? type.Id, requestedId !== defaultId ? requestedProduct : null)
    } else {
      const defaultProduct = products.find(item => getProductId(item) === defaultId)
      const initialProduct = requestedProduct ?? defaultProduct ?? (products.length === 1 ? products[0] : null)
      if (!initialProduct) throw new Error('Не удалось определить выбранный товар')
      selectProduct(initialProduct, false)
    }
  } catch (error) {
    if (requestId !== loadRequestId) return
    family.value = null
    legacyProduct.value = null
    configurator.reset()
    resetGallery()
    toast.error(error.message || 'Ошибка загрузки товара')
  } finally {
    if (requestId === loadRequestId) loading.value = false
  }
}

async function syncRouteProduct(productId) {
  const normalizedProductId = normalizeId(productId)
  if ([...internalNavigations.values()].includes(normalizedProductId)) {
    return
  }
  internalNavigations.clear()
  await loadFamily(normalizedProductId)
}

onBeforeUnmount(() => {
  ++loadRequestId
  internalNavigations.clear()
})

async function loadCart() {
  if (!authStore.isAuthenticated) return

  const userId = authStore.user?.id ?? authStore.user?.Id ?? null
  if (!userId) return

  try {
    cartLoading.value = true
    await cartStore.loadCartByUserId(userId)
  } catch (error) {
    toast.error(error.message || 'Ошибка загрузки проектов')
  } finally {
    cartLoading.value = false
  }
}

function goBack() {
  router.back()
}

function formatPrice(value) {
  if (value == null || value === '') {
    return '0 ₸'
  }

  const number = Number(value)

  if (!Number.isFinite(number)) {
    return '0 ₸'
  }

  return `${number.toLocaleString('ru-RU')} ₸`
}

function selectImage(image, index) {
  selectedImage.value = image.url
  selectedImageIndex.value = index
}

function showPrevImage() {
  if (productImages.value.length <= 1) return

  const prevIndex =
    selectedImageIndex.value === 0
      ? productImages.value.length - 1
      : selectedImageIndex.value - 1

  selectedImageIndex.value = prevIndex
  selectedImage.value = productImages.value[prevIndex].url
}

function showNextImage() {
  if (productImages.value.length <= 1) return

  const nextIndex =
    selectedImageIndex.value === productImages.value.length - 1
      ? 0
      : selectedImageIndex.value + 1

  selectedImageIndex.value = nextIndex
  selectedImage.value = productImages.value[nextIndex].url
}

function isHexColor(value) {
  if (!value) return false
  return /^#([0-9A-Fa-f]{3}|[0-9A-Fa-f]{6})$/.test(String(value).trim())
}

function isColorAttribute(attr) {
  const name = String(attr?.name || '').toLowerCase()
  return name.includes('цвет') || isHexColor(attr?.selectedValue)
}

function addToCart() {
  if (!canAddToCart.value) return
  showAddToCartModal.value = true
}

async function handleAddToCartConfirm(payload) {
  try {
    if (!product.value || !canAddToCart.value) return

    const confirmedProductId = getProductId(product.value)
    if (!confirmedProductId) return
    addToCartLoading.value = true

    const {
      cartId,
      newCartName,
      roomId,
      createNewCart,
      createNewRoom,
      newRoomName
    } = payload

    let targetCartId = cartId
    let targetRoomId = roomId

    if (createNewCart && newCartName) {
      targetCartId = await cartStore.createCart(newCartName)
      targetRoomId = null
    }

    if (!targetCartId) {
      throw new Error('Выберите проект или создайте новый')
    }

    if (createNewRoom && newRoomName) {
      await cartStore.createRoom({
        cartId: targetCartId,
        name: newRoomName
      })

      await cartStore.selectCart(targetCartId)

      const newRoom = cartStore.enrichedRooms.find(
        room => room.name === newRoomName
      )

      targetRoomId = newRoom?.id || null
    }

    await cartStore.addProductToCart({
      productId: confirmedProductId,
      productVariantId: null,
      cartId: targetCartId,
      roomId: targetRoomId,
      quantity: payload.quantity ?? 1
    })

    showAddToCartModal.value = false
    await cartStore.selectCart(targetCartId)

    toast.success('Товар добавлен в проект')
  } catch (error) {
    toast.error(error.message || 'Ошибка добавления товара в проект')
  } finally {
    addToCartLoading.value = false
  }
}

function closeImageModal() {
  showImageModal.value = false
}

function openImageModal() {
  if (!selectedImage.value) return
  showImageModal.value = true
}

function handleImageModalKeydown(event) {
  if (event.key === 'Escape' && showImageModal.value) closeImageModal()
}

onMounted(() => window.addEventListener('keydown', handleImageModalKeydown))
onBeforeUnmount(() => window.removeEventListener('keydown', handleImageModalKeydown))

watch(
  () => route.params.id,
  async newId => {
    await syncRouteProduct(newId)
  }
)

onMounted(async () => {
  await loadFamily(route.params.id)
  await loadCart()
})
</script>

<template>
  <div class="product-details-page">
    <div class="page-top">
      <Button label="Назад" icon="pi pi-arrow-left" size="small" text class="back-btn" @click="goBack" />
    </div>

    <div v-if="loading" class="state-block">
      Загрузка товара...
    </div>

    <div v-else-if="!family" class="state-block">
      Товар не найден
    </div>

    <div v-else class="product-layout">
      <section class="gallery-section">
        <div class="main-image-card">
          <button v-if="productImages.length > 1" class="gallery-arrow gallery-arrow-left" @click="showPrevImage">
            <i class="pi pi-chevron-left"></i>
          </button>

          <ProductImage :src="selectedImage" :alt="family?.productType?.name ?? family?.ProductType?.Name ?? 'Товар'"
            class="main-product-image" @click="openImageModal" />

          <button v-if="productImages.length > 1" class="gallery-arrow gallery-arrow-right" @click="showNextImage">
            <i class="pi pi-chevron-right"></i>
          </button>

          <div v-if="product?.saleIsActive ?? product?.SaleIsActive" class="sale-badge">
            Скидка
          </div>

          <div v-if="productImages.length > 1" class="image-counter">
            {{ selectedImageIndex + 1 }} / {{ productImages.length }}
          </div>
        </div>

        <div v-if="productImages.length > 1" class="images-preview">
          <button v-for="(img, index) in productImages" :key="img.id || img.url" class="preview-image-btn"
            :class="{ active: selectedImageIndex === index }" @click="selectImage(img, index)">
            <ProductImage :src="img.url" :alt="family?.productType?.name ?? family?.ProductType?.Name ?? 'Товар'" class="preview-image" />
          </button>
        </div>
      </section>

      <section class="info-section">
        <div class="product-main-info">
          <div class="product-kicker">
            {{ family?.productType?.brandName ?? family?.ProductType?.BrandName ?? 'IN RED BOX' }}
          </div>

          <h1 class="product-title">
            {{ family?.productType?.name ?? family?.ProductType?.Name ?? 'Без названия' }}
          </h1>

          <div class="product-tags">
            <span>Артикул: {{ displayedArticul }}</span>
            <span v-if="family?.productType?.categoryName ?? family?.ProductType?.CategoryName">
              {{ family?.productType?.categoryName ?? family?.ProductType?.CategoryName }}
            </span>
          </div>

          <p class="description">
            {{ family?.productType?.description ?? family?.ProductType?.Description ?? 'Описание отсутствует' }}
          </p>

          <div v-if="hasVariantAttributes" class="variant-selector">
            <div
              v-for="attribute in variantAttributes"
              :key="getAttributeId(attribute)"
              class="variant-finish-row"
              :class="{ 'has-value-groups': getAttributeValueGroups(attribute).some(group => group.name) }"
            >
              <span class="variant-label">
                {{ getAttributeName(attribute) }}
              </span>

              <div v-for="group in getAttributeValueGroups(attribute)" :key="group.id ?? 'ungrouped'"
                class="variant-value-group">
                <div v-if="group.name" class="variant-value-group-label">{{ group.name }}</div>
                <div class="variant-groups">
                  <button
                    v-for="value in group.values"
                    :key="getValueId(value)"
                    type="button"
                    class="variant-group-btn"
                    :class="{
                      active: isAttributeValueSelected(attribute, value),
                      unavailable: !isValueAvailable(attribute, value)
                    }"
                    :disabled="!isValueAvailable(attribute, value)"
                    @click="selectAttributeValue(attribute, value)"
                  >
                    {{ getValueText(value) }}
                  </button>
                </div>
              </div>
              <button v-if="selectedValues[getAttributeId(attribute)] != null" type="button"
                class="clear-selection" @click="configurator.clearSelection(getAttributeId(attribute))">
                Сбросить
              </button>
            </div>

            <div class="variant-status" aria-live="polite" :aria-busy="variantLoading">
              <span v-if="variantLoading">Обновление вариантов...</span>
              <template v-else-if="variantError">
                <span>{{ variantError.message || 'Не удалось загрузить варианты' }}</span>
                <button type="button" @click="configurator.queryAvailability()">Повторить</button>
              </template>
              <span v-else-if="matchingProductsCount === 0">Нет подходящего товара. Выберите другой доступный вариант или сбросьте выбор.</span>
              <span v-else-if="!product">Выберите характеристики, чтобы определить товар.</span>
              <button type="button" class="clear-selection" @click="configurator.clearSelections()">Сбросить всё</button>
            </div>

            <div class="variant-summary">
              <span class="variant-summary-item">
                Артикул: {{ displayedArticul }}
              </span>
            </div>
          </div>

          <div
            v-else-if="needsExplicitProductSelection"
            class="variant-selector"
          >
            <div class="variant-finish-row">
              <label for="product-select" class="variant-label">
                Вариант товара
              </label>

              <select
                id="product-select"
                v-model="explicitProductId"
                class="finish-dropdown"
              >
                <option
                  v-for="familyProduct in familyProducts"
                  :key="getProductId(familyProduct)"
                  :value="getProductId(familyProduct)"
                >
                  {{ familyProduct.articul ?? familyProduct.Articul ?? `#${getProductId(familyProduct)}` }}
                </option>
              </select>
            </div>
          </div>
          <div class="price-block">
            <span v-if="displayedOldPrice" class="old-price">
              {{ displayedOldPrice }}
            </span>

            <span class="actual-price">
              {{ displayedPrice }}
            </span>
          </div>

          <div class="actions">
            <Button
              label="В проект"
              icon="pi pi-shopping-cart"
              class="cart-btn"
              :disabled="addToCartLoading || !canAddToCart"
              :loading="addToCartLoading"
              @click="addToCart"
            />
            </div>
        </div>
        <div v-if="displayedAttributes.length" class="details-block">
          <div class="details-header">
            <span>Детали</span>
            <h2>Характеристики</h2>
          </div>

          <div class="details-content">
            <div class="attributes-list">
              <div v-for="attr in displayedAttributes" :key="attr.id" class="attribute-row">
                <span class="attr-name">
                  {{ attr.name }}
                </span>

                <span class="attr-value">
                  <template v-if="isColorAttribute(attr) && isHexColor(attr.selectedValue)">
                    <span class="color-dot" :style="{ backgroundColor: attr.selectedValue }"></span>
                    {{ attr.selectedValue }}
                  </template>

                  <template v-else>
                    {{ attr.selectedValue || '—' }}
                  </template>
                </span>
              </div>
            </div>

            
          </div>
        </div>
      </section>
    </div>

    <AddToCartModal
      :visible="showAddToCartModal"
      :carts="cartStore.carts"
      :product="modalProduct"
      :loading="addToCartLoading"
      @close="showAddToCartModal = false"
      @confirm="handleAddToCartConfirm"
    />

    <div v-if="showImageModal" class="image-modal" @click="closeImageModal">
      <button class="modal-close" @click.stop="closeImageModal">
        <i class="pi pi-times"></i>
      </button>

      <button v-if="productImages.length > 1" class="modal-arrow modal-arrow-left" @click.stop="showPrevImage">
        <i class="pi pi-chevron-left"></i>
      </button>

      <img :src="selectedImage" :alt="family?.productType?.name ?? family?.ProductType?.Name ?? 'Товар'" class="modal-image" @click.stop="closeImageModal" />

      <button v-if="productImages.length > 1" class="modal-arrow modal-arrow-right" @click.stop="showNextImage">
        <i class="pi pi-chevron-right"></i>
      </button>
    </div>
  </div>
</template>

<style scoped>
.variant-group-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}
.variant-group-btn:disabled:hover {
  background: transparent;
  color: inherit;
}
.variant-status {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: 12px;
}
.clear-selection {
  background: transparent;
  border: 0;
  color: #8a8178;
  cursor: pointer;
  text-decoration: underline;
}

.product-details-page {
  min-height: 100vh;
  padding: 34px 28px 72px;
  background: #ffffff;
  color: #171412;
}

.page-top {
  max-width: 1320px;
  margin: 0 auto 24px;
}

:deep(.back-btn.p-button) {
  min-height: 44px;
  padding: 0.45rem 0.65rem !important;
  font-size: 0.875rem;
  border-radius: 6px !important;
  color: #171412 !important;
  border-color: transparent !important;
  background: transparent !important;
  box-shadow: none !important;
}

:deep(.back-btn.p-button:hover),
:deep(.back-btn.p-button:active) {
  color: #c41217 !important;
  border-color: transparent !important;
  background: rgba(196, 18, 23, 0.06) !important;
  box-shadow: none !important;
}

:deep(.back-btn.p-button:focus-visible) {
  outline: 2px solid #c41217;
  outline-offset: 3px;
}

.state-block {
  max-width: 1320px;
  margin: 0 auto;
  padding: 58px 34px;
  text-align: center;
  border: 1px dashed rgba(33, 27, 22, 0.18);
  color: #7c7168;
  background: rgba(255, 255, 255, 0.68);
  font-size: 15px;
}

.product-layout {
  max-width: 1320px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: minmax(0, 1.04fr) minmax(380px, 0.96fr);
  gap: 34px;
  align-items: start;
}

/* Gallery */
.gallery-section {
  position: sticky;
  top: 24px;
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.main-image-card {
  position: relative;
  overflow: hidden;
  aspect-ratio: 1 / 1.04;
  background: #eee8e1;
  border: 1px solid rgba(33, 27, 22, 0.08);
  border-radius: 0 56px 0 0;
  box-shadow: 0 24px 60px rgba(33, 27, 22, 0.12);
}

.main-product-image {
  position: absolute;
  inset: 0;
  height: 100%;
  --product-image-ratio: auto;
  cursor: zoom-in;
}

.gallery-arrow {
  position: absolute;
  top: 50%;
  z-index: 3;
  transform: translateY(-50%);
  width: 48px;
  height: 48px;
  border: none;
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(8px);
  color: white;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: all 0.3s ease;
  opacity: 0;
  visibility: hidden;
}

.main-image-card:hover .gallery-arrow {
  opacity: 1;
  visibility: visible;
}

.gallery-arrow i {
  font-size: 24px;
  font-weight: 300;
}

.gallery-arrow:hover {
  background: #c41217;
  transform: translateY(-50%) scale(1.1);
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
}

.gallery-arrow:active {
  transform: translateY(-50%) scale(0.95);
}

.gallery-arrow-left {
  left: 20px;
}

.gallery-arrow-right {
  right: 20px;
}

.image-counter {
  position: absolute;
  bottom: 16px;
  right: 16px;
  z-index: 3;
  background: rgba(0, 0, 0, 0.65);
  backdrop-filter: blur(8px);
  color: #fff;
  padding: 6px 14px;
  border-radius: 30px;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.5px;
}

.sale-badge {
  position: absolute;
  top: 18px;
  left: 0;
  z-index: 3;
  background: #c41217;
  color: #ffffff;
  font-size: 11px;
  font-weight: 700;
  padding: 7px 18px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  border-radius: 0 20px 20px 0;
}

.images-preview {
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  gap: 10px;
}

.preview-image-btn {
  position: relative;
  border: 1px solid rgba(33, 27, 22, 0.1);
  background: #eee8e1;
  padding: 0;
  aspect-ratio: 1 / 1;
  overflow: hidden;
  cursor: pointer;
  transition: 0.2s ease;
}

.preview-image-btn.active {
  border-color: #c41217;
  box-shadow: 0 0 0 2px rgba(196, 18, 23, 0.12);
}

.preview-image-btn:hover {
  transform: translateY(-2px);
  border-color: #c41217;
}

.preview-image {
  width: 100%;
  height: 100%;
  --product-image-padding: 6px;
}

.info-section {
  display: flex;
  flex-direction: column;
  gap: 22px;
}

.product-main-info {
  padding: 34px;
  background: rgba(255, 252, 248, 0.84);
  border: 1px solid rgba(33, 27, 22, 0.08);
  box-shadow: 0 18px 48px rgba(33, 27, 22, 0.08);
}

.product-kicker {
  margin-bottom: 14px;
  color: #c41217;
  font-size: 12px;
  letter-spacing: 0.18em;
  text-transform: uppercase;
}

.product-title {
  margin: 0;
  max-width: 680px;
  font-family: Georgia, "Times New Roman", serif;
  font-size: clamp(38px, 5vw, 68px);
  line-height: 0.95;
  font-weight: 400;
  letter-spacing: -0.07em;
  text-transform: uppercase;
  color: #171412;
}

.product-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 18px;
}

.product-tags span {
  padding: 7px 10px;
  background: #f1ece6;
  color: #70675f;
  font-size: 12px;
  line-height: 1;
}

.description {
  margin: 24px 0 0;
  max-width: 620px;
  color: #6f665f;
  line-height: 1.75;
  font-size: 15px;
}

.variant-selector {
  margin-top: 22px;
  display: grid;
  gap: 16px;
  padding: 20px 0 0;
  border-top: 1px solid rgba(33, 27, 22, 0.08);
}

.variant-group-row,
.variant-finish-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 14px;
}

.variant-label {
  color: #60594f;
  font-size: 13px;
  text-transform: uppercase;
  letter-spacing: 0.12em;
}

.variant-value-group-label {
  margin-bottom: 8px;
  font-weight: 600;
}

.variant-finish-row.has-value-groups {
  flex-direction: column;
  align-items: flex-start;
}

.variant-groups {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.variant-group-btn {
  border: 1px solid rgba(33, 27, 22, 0.16);
  background: #fff;
  color: #171412;
  padding: 10px 14px;
  cursor: pointer;
  transition: border-color 0.2s ease, background 0.2s ease;
}

.variant-group-btn.active,
.variant-group-btn:hover {
  border-color: #c41217;
  background: rgba(196, 18, 23, 0.08);
}

.finish-dropdown {
  min-width: 220px;
  padding: 10px 12px;
  border: 1px solid rgba(33, 27, 22, 0.16);
  border-radius: 4px;
  background: #fff;
  color: #171412;
}

.variant-summary {
  display: flex;
  flex-wrap: wrap;
  gap: 18px;
  color: #453f38;
  font-size: 14px;
}

.variant-summary-item {
  font-weight: 600;
}

.price-block {
  display: flex;
  align-items: baseline;
  gap: 12px;
  margin-top: 28px;
  flex-wrap: wrap;
}

.old-price {
  text-decoration: line-through;
  color: #aaa29a;
  font-size: 17px;
}

.actual-price {
  color: #171412;
  font-size: 32px;
  line-height: 1;
  font-weight: 800;
}

.actions {
  margin-top: 24px;
}

:deep(.cart-btn.p-button) {
  width: 100%;
  height: 52px;
  border-radius: 0 !important;
  background: #c41217;
  border-color: #c41217;
  color: #fff;
  font-size: 14px;
  font-weight: 700;
}

:deep(.cart-btn.p-button:hover) {
  background: #9f0f13;
  border-color: #9f0f13;
}

:deep(.cart-btn.p-button:disabled) {
  background: #e5e0da !important;
  border-color: #e5e0da !important;
  color: #7c7168 !important;
  opacity: 1 !important;
  cursor: not-allowed;
}

.details-block {
  padding: 30px;
  background: rgba(255, 255, 255, 0.72);
  border: 1px solid rgba(33, 27, 22, 0.08);
}

.details-header {
  margin-bottom: 20px;
}

.details-header span {
  color: #c41217;
  font-size: 12px;
  letter-spacing: 0.18em;
  text-transform: uppercase;
}

.details-header h2 {
  margin: 8px 0 0;
  font-family: Georgia, "Times New Roman", serif;
  font-size: 38px;
  line-height: 1;
  font-weight: 400;
  letter-spacing: -0.06em;
  text-transform: uppercase;
}

.details-content {
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: 20px;
  align-items: start;
}

.attributes-list {
  display: flex;
  flex-direction: column;
}

.attribute-row {
  display: grid;
  grid-template-columns: 0.9fr 1.1fr;
  gap: 16px;
  padding: 14px 0;
  border-bottom: 1px solid rgba(33, 27, 22, 0.08);
}

.attribute-row:first-child {
  border-top: 1px solid rgba(33, 27, 22, 0.08);
}

.attr-name {
  color: #8a8178;
  font-size: 14px;
}

.attr-value {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 9px;
  color: #171412;
  font-size: 14px;
  font-weight: 600;
  text-align: right;
}

.color-dot {
  width: 23px;
  height: 23px;
  display: inline-flex;
  flex-shrink: 0;
  border-radius: 50%;
  border: 1px solid rgba(33, 27, 22, 0.18);
  box-shadow:
    inset 0 0 0 2px rgba(255, 255, 255, 0.55),
    0 5px 12px rgba(33, 27, 22, 0.12);
}

.details-image-card {
  overflow: hidden;
  aspect-ratio: 0.76 / 1;
  background: #eee8e1;
  border-radius: 44px 0 0 0;
}

.details-image-card img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  cursor: zoom-in;
  transition: transform 0.42s ease;
}

.details-image-card:hover img {
  transform: scale(1.05);
}

.image-modal {
  position: fixed;
  inset: 0;
  z-index: 200;
  background: rgba(13, 10, 8, 0.82);
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 3vw;
  backdrop-filter: blur(10px);
}

.modal-image {
  width: 94vw;
  height: 88vh;
  height: 88dvh;
  max-width: 100%;
  max-height: 100%;
  object-position: center;
  object-fit: contain;
  box-shadow: 0 28px 80px rgba(0, 0, 0, 0.38);
}

.modal-close,
.modal-arrow {
  position: fixed;
  z-index: 210;
  border: 1px solid rgba(255, 255, 255, 0.28);
  background: rgba(255, 255, 255, 0.1);
  color: #fff;
  cursor: pointer;
  display: grid;
  place-items: center;
  backdrop-filter: blur(12px);
  transition: 0.2s ease;
}

.modal-close:hover,
.modal-arrow:hover {
  background: #c41217;
  border-color: #c41217;
}

.modal-close {
  top: 24px;
  right: 24px;
  width: 44px;
  height: 44px;
}

.modal-arrow {
  top: 50%;
  transform: translateY(-50%);
  width: 48px;
  height: 48px;
}

.modal-arrow-left {
  left: 28px;
}

.modal-arrow-right {
  right: 28px;
}

@media (max-width: 1100px) {
  .product-layout {
    grid-template-columns: 1fr;
  }

  .gallery-section {
    position: static;
  }

  .main-image-card {
    aspect-ratio: 1.18 / 1;
  }

  .details-content {
    grid-template-columns: minmax(0, 1fr) 220px;
  }
}

@media (max-width: 760px) {
  .product-details-page {
    padding: 22px 14px 52px;
  }

  .product-main-info,
  .details-block {
    padding: 22px;
  }

  .main-image-card {
    border-radius: 0 34px 0 0;
    aspect-ratio: 1 / 1.08;
  }

  .product-title {
    font-size: 38px;
  }

  .actual-price {
    font-size: 27px;
  }

  .images-preview {
    grid-template-columns: repeat(4, minmax(0, 1fr));
  }

  .details-content {
    grid-template-columns: 1fr;
  }

  .details-image-card {
    max-height: 320px;
    aspect-ratio: 1.4 / 1;
    border-radius: 34px 0 0 0;
  }

  .attribute-row {
    grid-template-columns: 1fr;
    gap: 6px;
  }

  .attr-value {
    justify-content: flex-start;
    text-align: left;
  }

  .gallery-arrow {
    width: 38px;
    height: 38px;
  }

  .gallery-arrow-left {
    left: 12px;
  }

  .gallery-arrow-right {
    right: 12px;
  }

  .modal-arrow {
    width: 42px;
    height: 42px;
  }

  .modal-arrow-left {
    left: 14px;
  }

  .modal-arrow-right {
    right: 14px;
  }

  .modal-close {
    top: 16px;
    right: 16px;
  }
}

@media (max-width: 480px) {

  .product-main-info,
  .details-block {
    padding: 18px;
  }

  .product-title {
    font-size: 32px;
  }

  .product-tags {
    flex-direction: column;
    align-items: flex-start;
  }

  .images-preview {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }

  .details-header h2 {
    font-size: 30px;
  }

  .image-modal {
    padding: 18px;
  }

  .modal-image {
    width: 94vw;
    height: 88vh;
    height: 88dvh;
  }
}

@media (max-width: 768px) {
  .gallery-arrow {
    opacity: 0.7;
    visibility: visible;
    width: 40px;
    height: 40px;
  }

  .gallery-arrow i {
    font-size: 20px;
  }

  .gallery-arrow-left {
    left: 12px;
  }

  .gallery-arrow-right {
    right: 12px;
  }
}
</style>
